import { Navigate, Outlet, useLocation } from "react-router";
import type { AppView, Role } from "@/contracts";

import { FullPageSpinner } from "@/components/ui/spinner";
import { hasAnyView, hasRole, hasView, useAuth } from "./auth-context";

/**
 * Gate for everything behind a session.
 *
 * `replace` keeps the protected URL out of history, so the back button after
 * logging out does not bounce between the guard and the login form. The
 * attempted path travels in location state and the login screen returns the
 * user to it.
 */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <FullPageSpinner label="Checking your session…" />;
  }

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
}

/**
 * Second gate, by role.
 *
 * This only hides screens. The API re-checks every permission on its own
 * because anything decided in a browser can be edited in a browser.
 */
export function RequireRole({ allowed }: { allowed: readonly Role[] }) {
  const { user } = useAuth();

  if (!hasRole(user, allowed)) {
    return <Navigate to="/profile" replace />;
  }

  return <Outlet />;
}

/**
 * Gate by membership view (ADR-088). Deep links to a denied module land on
 * profile rather than dashboard, because dashboard itself may be ungated.
 */
export function RequireView({ view }: { view: AppView }) {
  const { user } = useAuth();

  if (!hasView(user, view)) {
    return <Navigate to="/profile" replace />;
  }

  return <Outlet />;
}

/**
 * Gate when a screen covers more than one grantable view (e.g. the contracts
 * hub). Deep links still land on profile when none of the views are held.
 */
export function RequireAnyView({ views }: { views: readonly AppView[] }) {
  const { user } = useAuth();

  if (!hasAnyView(user, views)) {
    return <Navigate to="/profile" replace />;
  }

  return <Outlet />;
}
